import { ArrowLeft, MoreHorizontal } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { Link, Navigate, useNavigate, useParams } from 'react-router'
import AdminUserAccountFields from '../../components/admin/AdminUserAccountFields'
import AdminActivitySummary from '../../components/admin/AdminActivitySummary'
import AdminUserWatchlistSection from '../../components/admin/AdminUserWatchlistSection'
import AdminUserStatusDot from '../../components/admin/AdminUserStatusDot'
import { adminUserStatusLabel, adminUserStatus } from '../../components/admin/adminUserStatus'
import { Menu, MenuItem, MenuSeparator, Modal } from '../../components/ui'
import { useAuth } from '../../contexts/AuthContext'
import { useToast } from '../../contexts/ToastContext'
import {
  deleteAdminUser,
  fetchAdminOrganizations,
  fetchAdminUserDetail,
  resendAdminInvite,
  setAdminUserActive,
  type AdminOrganization,
  type AdminUserDetail,
} from '../../services/admin'
import { formatDate, formatDateTimeDa } from '../../utils/format'

const inputClass =
  'w-full border border-rule rounded-sm bg-paper px-2.5 py-1.5 text-sm text-ink placeholder:text-ink-300 focus:border-ink focus:outline-none focus:ring-1 focus:ring-ink/10'
const sectionClass = 'border border-rule rounded-sm p-3'
const sectionTitleClass = 'text-xs uppercase tracking-wide text-ink-500 font-medium mb-2'

function inviteStatusLabel(invite: AdminUserDetail['invites'][number]): string {
  if (invite.usedAt) return 'Brugt'
  const expires = new Date(
    invite.expiresAt.includes('T') ? invite.expiresAt : `${invite.expiresAt.replace(' ', 'T')}Z`
  )
  if (!Number.isNaN(expires.getTime()) && expires.getTime() < Date.now()) return 'Udløbet'
  return 'Afventer'
}

const AdminUserProfilePage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>()
  const navigate = useNavigate()
  const { user: currentUser } = useAuth()
  const { showToast } = useToast()

  const [detail, setDetail] = useState<AdminUserDetail | null>(null)
  const [organizations, setOrganizations] = useState<AdminOrganization[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [deleteConfirm, setDeleteConfirm] = useState('')
  const [showDelete, setShowDelete] = useState(false)
  const [deleteBusy, setDeleteBusy] = useState(false)

  const targetId = Number(userId)
  const isSelf = detail?.user.id === currentUser?.id

  const load = useCallback(async () => {
    if (!Number.isFinite(targetId) || targetId <= 0) {
      setError('Ugyldig bruger.')
      setLoading(false)
      return
    }
    setError(null)
    try {
      const [data, orgs] = await Promise.all([
        fetchAdminUserDetail(targetId),
        fetchAdminOrganizations(),
      ])
      setDetail(data)
      setOrganizations(orgs)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Kunne ikke hente bruger')
    } finally {
      setLoading(false)
    }
  }, [targetId])

  useEffect(() => {
    void load()
  }, [load])

  if (!currentUser) return null
  if (!currentUser.isAdmin) return <Navigate to="/account" replace />

  async function refreshUser() {
    const data = await fetchAdminUserDetail(targetId)
    setDetail(data)
  }

  async function toggleActive() {
    if (!detail) return
    try {
      await setAdminUserActive(detail.user.id, !detail.user.isActive)
      await refreshUser()
      showToast(detail.user.isActive ? 'Bruger deaktiveret.' : 'Bruger aktiveret.')
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Handling fejlede')
    }
  }

  async function handleResendInvite() {
    if (!detail) return
    try {
      const { mailDelivery } = await resendAdminInvite(detail.user.id)
      await refreshUser()
      const mailNote = mailDelivery === 'log' ? ' (dev-log)' : ' på e-mail'
      showToast(`Invitation sendt${mailNote}.`)
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Send fejlede')
    }
  }

  async function handleDelete() {
    if (!detail) return
    setDeleteBusy(true)
    try {
      await deleteAdminUser(detail.user.id, deleteConfirm.trim())
      showToast('Bruger slettet.')
      navigate('/account/admin')
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Slet fejlede')
    } finally {
      setDeleteBusy(false)
    }
  }

  if (loading) {
    return <p className="text-sm text-ink-500">Henter bruger…</p>
  }

  if (error || !detail) {
    return (
      <div>
        <Link
          to="/account/admin"
          className="inline-flex items-center gap-1 text-sm text-ink-500 hover:text-ink mb-4"
        >
          <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
          Brugerstyring
        </Link>
        <p className="text-sm text-signal" role="alert">
          {error ?? 'Bruger ikke fundet.'}
        </p>
      </div>
    )
  }

  const { user, watchlist, invites, activity } = detail
  const status = adminUserStatus(user)

  return (
    <div>
      <Link
        to="/account/admin"
        className="inline-flex items-center gap-1 text-sm text-ink-500 hover:text-ink mb-4"
      >
        <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
        Brugerstyring
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="font-serif text-2xl text-ink">{user.name}</h1>
            <AdminUserStatusDot user={user} />
            {user.isAdmin && (
              <span className="text-[10px] uppercase text-signal">admin</span>
            )}
          </div>
          <p className="text-sm text-ink-500 mt-0.5">{adminUserStatusLabel(status)}</p>
        </div>

        <Menu
          align="right"
          dense
          portal
          trigger={<MoreHorizontal className="h-4 w-4" aria-hidden />}
          triggerAriaLabel={`Handlinger for ${user.name}`}
          triggerClassName="p-1.5 border border-rule rounded-sm hover:bg-paper-200/60"
        >
          {(close) => (
            <>
              <MenuItem
                disabled={isSelf}
                onClick={() => {
                  close()
                  void toggleActive()
                }}
              >
                {user.isActive ? 'Deaktiver' : 'Aktiver'}
              </MenuItem>
              <MenuItem
                onClick={() => {
                  close()
                  void handleResendInvite()
                }}
              >
                {user.passwordReady ? 'Nulstil adgangskode' : 'Gensend invitation'}
              </MenuItem>
              <MenuSeparator />
              <MenuItem
                danger
                disabled={isSelf}
                onClick={() => {
                  close()
                  setShowDelete(true)
                  setDeleteConfirm('')
                }}
              >
                Slet bruger
              </MenuItem>
            </>
          )}
        </Menu>
      </div>

      <div className="grid gap-4 lg:grid-cols-2 mb-6">
        <section className={sectionClass}>
          <h2 className={sectionTitleClass}>Konto</h2>
          <AdminUserAccountFields
            user={user}
            organizations={organizations}
            isSelf={!!isSelf}
            onUpdated={(updated) => {
              setDetail((prev) => (prev ? { ...prev, user: updated } : prev))
            }}
            onToast={showToast}
          />
          <dl className="mt-3 pt-3 border-t border-rule space-y-2 text-xs text-ink">
            <div className="flex justify-between gap-4">
              <dt className="text-ink-500">Oprettet</dt>
              <dd className="tabular-nums">{formatDateTimeDa(user.createdAt) ?? '—'}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-ink-500">Seneste login</dt>
              <dd className="tabular-nums">{formatDateTimeDa(user.lastLoginAt) ?? '—'}</dd>
            </div>
          </dl>
        </section>

        <section className={sectionClass}>
          <h2 className={sectionTitleClass}>Watchlist</h2>
          <AdminUserWatchlistSection userId={user.id} watchlist={watchlist} />
        </section>
      </div>

      <section className={`${sectionClass} mb-4`}>
        <h2 className={sectionTitleClass}>Seneste aktivitet</h2>
        {activity.length === 0 ? (
          <p className="text-xs text-ink-400">Ingen aktivitet logget endnu.</p>
        ) : (
          <ul className="divide-y divide-rule">
            {activity.map((entry) => (
              <li
                key={entry.id}
                className="flex items-start justify-between gap-3 py-2 text-xs"
              >
                <span className="text-ink">
                  <AdminActivitySummary summary={entry.summary} meta={entry.meta} />
                </span>
                <time
                  className="shrink-0 text-ink-500 tabular-nums"
                  dateTime={entry.createdAt}
                >
                  {formatDateTimeDa(entry.createdAt)}
                </time>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className={sectionClass}>
        <h2 className={sectionTitleClass}>Invitationer</h2>
        {invites.length === 0 ? (
          <p className="text-xs text-ink-400">Ingen invitationer.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs text-left">
              <thead>
                <tr className="text-ink-500 uppercase tracking-wide">
                  <th className="py-1.5 pr-3 font-medium">Type</th>
                  <th className="py-1.5 pr-3 font-medium">Sendt</th>
                  <th className="py-1.5 pr-3 font-medium">Udløber</th>
                  <th className="py-1.5 font-medium">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-rule text-ink">
                {invites.map((invite, i) => (
                  <tr key={`${invite.createdAt}-${i}`}>
                    <td className="py-1.5 pr-3">
                      {invite.purpose === 'invite' ? 'Invitation' : 'Nulstil'}
                    </td>
                    <td className="py-1.5 pr-3 tabular-nums">
                      {formatDateTimeDa(invite.createdAt) ?? '—'}
                    </td>
                    <td className="py-1.5 pr-3 tabular-nums">
                      {formatDate(invite.expiresAt) ?? '—'}
                    </td>
                    <td className="py-1.5">{inviteStatusLabel(invite)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <Modal
        open={showDelete}
        onClose={() => {
          setShowDelete(false)
          setDeleteConfirm('')
        }}
        title="Slet bruger permanent?"
        size="md"
        footer={
          <>
            <button
              type="button"
              onClick={() => {
                setShowDelete(false)
                setDeleteConfirm('')
              }}
              className="px-3 py-1.5 text-sm text-ink-500"
            >
              Annuller
            </button>
            <button
              type="button"
              disabled={
                deleteBusy ||
                deleteConfirm.trim().toLowerCase() !== user.email.toLowerCase()
              }
              onClick={() => void handleDelete()}
              className="px-3 py-1.5 bg-signal text-paper text-sm rounded-sm disabled:opacity-50"
            >
              {deleteBusy ? 'Sletter…' : 'Slet permanent'}
            </button>
          </>
        }
      >
        <p className="text-sm text-ink-500 mb-3">
          Watchlist, aktivitet, notifikationer og konto for{' '}
          <strong className="text-ink">{user.name}</strong> slettes. Et fuldt snapshot gemmes i
          sletningsloggen til gendannelse.
        </p>
        <p className="text-sm text-ink mb-2">Skriv brugerens e-mail for at bekræfte:</p>
        <input
          type="email"
          className={inputClass}
          value={deleteConfirm}
          onChange={(e) => setDeleteConfirm(e.target.value)}
          placeholder={user.email}
          aria-label="Bekræft e-mail for sletning"
        />
      </Modal>
    </div>
  )
}

export default AdminUserProfilePage
